import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Search, MapPin, Phone, Mail, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Sucursales = () => {
  const [sucursales, setSucursales] = useState([]);
  const [filteredSucursales, setFilteredSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    direccion: '',
    telefono: '',
    email: '',
    activa: true,
  });

  useEffect(() => {
    loadSucursales();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [sucursales, searchTerm]);

  const loadSucursales = async () => {
    try {
      const response = await axios.get(`${API}/sucursales`);
      setSucursales(response.data);
    } catch (error) {
      console.error('Error loading sucursales:', error);
      toast.error('Error al cargar sucursales');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...sucursales];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.nombre.toLowerCase().includes(term) ||
          s.direccion?.toLowerCase().includes(term)
      );
    }

    setFilteredSucursales(filtered);
  };

  const handleOpenDialog = (sucursal = null) => {
    if (sucursal) {
      setEditingId(sucursal.id);
      setFormData({
        nombre: sucursal.nombre || '',
        descripcion: sucursal.descripcion || '',
        direccion: sucursal.direccion || '',
        telefono: sucursal.telefono || '',
        email: sucursal.email || '',
        activa: sucursal.activa ?? true,
      });
    } else {
      setEditingId(null);
      setFormData({
        nombre: '',
        descripcion: '',
        direccion: '',
        telefono: '',
        email: '',
        activa: true,
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.nombre) {
        toast.error('El nombre es requerido');
        return;
      }

      if (editingId) {
        await axios.put(`${API}/sucursales/${editingId}`, formData);
        toast.success('Sucursal actualizada');
      } else {
        await axios.post(`${API}/sucursales`, formData);
        toast.success('Sucursal creada');
      }
      setShowDialog(false);
      loadSucursales();
    } catch (error) {
      console.error('Error saving sucursal:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar sucursal');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/sucursales/${id}`);
      toast.success('Sucursal eliminada');
      loadSucursales();
    } catch (error) {
      console.error('Error deleting sucursal:', error);
      toast.error('Error al eliminar sucursal');
    }
    setDeleteId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Sucursales</h1>
              <p className="text-muted-foreground mt-1">{sucursales.length} sucursales</p>
            </div>
            <Button onClick={() => handleOpenDialog()} data-testid="new-sucursal-button">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Sucursal
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Filtros */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o dirección..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-sucursal-input"
              />
            </div>
          </div>
        </Card>

        {filteredSucursales.length === 0 ? (
          <Card className="p-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {sucursales.length === 0 ? 'No hay sucursales creadas' : 'No se encontraron resultados'}
            </p>
            {sucursales.length === 0 && (
              <Button className="mt-4" onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Crear primera sucursal
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredSucursales.map((sucursal) => (
              <Card key={sucursal.id} className="p-6" data-testid="sucursal-card">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{sucursal.nombre}</h3>
                      <Badge variant={sucursal.activa ? 'default' : 'secondary'}>
                        {sucursal.activa ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(sucursal)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(sucursal.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {sucursal.descripcion && (
                  <p className="text-sm text-muted-foreground mb-4">{sucursal.descripcion}</p>
                )}

                <div className="space-y-2 text-sm">
                  {sucursal.direccion && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{sucursal.direccion}</span>
                    </div>
                  )}
                  {sucursal.telefono && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{sucursal.telefono}</span>
                    </div>
                  )}
                  {sucursal.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span>{sucursal.email}</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog para crear/editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar' : 'Nueva'} Sucursal</DialogTitle>
            <DialogDescription>Completa la información de la sucursal</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Sucursal Centro"
                data-testid="sucursal-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción de la sucursal..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="direccion">Dirección</Label>
              <Input
                id="direccion"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                placeholder="Av. 18 de Julio 1234"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="+598 99 123 456"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="sucursal@email.com"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="activa">Sucursal activa</Label>
              <Switch
                id="activa"
                checked={formData.activa}
                onCheckedChange={(checked) => setFormData({ ...formData, activa: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} data-testid="save-sucursal-button">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar sucursal?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Los usuarios y lotes asociados perderán la referencia a esta sucursal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Sucursales;
